// DataTable.tsx
import { DragEvent, useEffect, useMemo, useRef, useState } from 'react';

import './styles.scss';
import { TableRow } from './hook/types';
import { useTableEngine } from './hook/useTableEngine';
import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { AppRowCar, DataTableProps, reorder, useColumnResize } from './fn';
import { FilterIcon } from '../../../CustomersV2/Modal/FilterIcon';
import FilterHeaderVehicleVersionTable from './Modal/FilterHeaderVehicleVersionTable';
import { NodataTable } from '../../../../components/NoDataTable';
import { noData } from '../../../../components/ExportImgFigma';
import { Spin } from 'antd';
import { CustomLoadingOverlayTable } from '../../../../until';
import { selectorCategoryCarIds } from '../../../../store/slices/categoryCarSlice';
import { useSelector } from 'react-redux';

export function VehicleVersionTable<T>(props: DataTableProps<T>) {

    const permittedCategoryCarIds = useSelector(selectorCategoryCarIds)
    const [selectedColumnFilter, setSelectedColumnFilter] = useState<string>();

    const {
        ischeckBox, columns, className, stickyHeader = true,
        filterColumn = [], nonResizableColumnIds = [], nonDraggableColumnIds = [],
        dataFilterList, loadingTable,
        rowClassName, onRowClick, setDataFilterList
    } = props;

    const {
        state, rows,
        setGlobalFilter, setColumnFilter, toggleSort,
        toggleSelectAllPage, toggleSelectRow, startEdit, stopEdit
    } = useTableEngine<T>(props);

    const isResizable = (id: string) =>
        !nonResizableColumnIds.includes(id);

    const isDraggable = (id: string) =>
        !nonDraggableColumnIds.includes(id);

    const allColumns = columns;

    const tableRef = useRef<HTMLTableElement>(null);
    const { widths, onMouseDown, onDoubleClick } = useColumnResize(tableRef, allColumns);

    const [colOrder, setColOrder] = useState<string[]>(() => allColumns.map(c => c.id));
    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [dropPreview, setDropPreview] = useState<{ targetId: string; side: 'left' | 'right' } | null>(null);

    const isAllSelected = rows.length > 0 && rows.every(r => state.selected.has(r.id));

    const [selectedRowId, setSelectedRowId] = useState<string | number>('');

    const orderedColumns = useMemo(() => {
        const map = new Map(colOrder.map((id, idx) => [id, idx]));
        return allColumns.slice().sort((a, b) => (map.get(a.id)! - map.get(b.id)!));
    }, [allColumns, colOrder]);

    const getDropSide = (thEl: HTMLTableCellElement, clientX: number): 'left' | 'right' => {
        const rect = thEl.getBoundingClientRect();
        return clientX < rect.left + rect.width / 2 ? 'left' : 'right';
    };

    const handleRowClick = (row: TableRow<T>, rowIndex: number) => {
        // console.log(row?.data);
        const dataRow = row.data as AppRowCar
        // console.log(dataRow?.categoryCarId)
        if (!dataRow?.categoryCarId || !permittedCategoryCarIds?.includes(dataRow?.categoryCarId)) {
            return;
        }
        // setSelectedRowId(prev => {
        //     // Nếu click trùng row đang chọn → bỏ chọn
        //     if (prev === row.id) {
        //         // callback trả về rỗng
        //         onRowClick?.(null as any, -1);
        //         return '';
        //     }

        //     // Click row mới
        //     onRowClick?.(row.data, rowIndex);
        //     return row.id;
        // });
        const [rowId, stt] = String(row.id).split("-");
        if (selectedRowId === rowId) {
            setSelectedRowId('');
            onRowClick?.(null as any, -1);
            return;
        }

        setSelectedRowId(rowId);
        onRowClick?.(row?.data, rowIndex);
    };

    const handleDragStart = (cid: string) => (e: DragEvent) => {
        setDraggingId(cid);
        e.dataTransfer.setData('text/x-col-id', cid);
        const th = tableRef.current?.querySelector<HTMLTableCellElement>(`th[data-cid="${cid}"]`);
        if (th && e.dataTransfer.setDragImage) {
            e.dataTransfer.setDragImage(th, th.clientWidth / 2, th.clientHeight / 2);
        }
    };

    const handleDragOver = (targetId: string) => (e: DragEvent) => {
        e.preventDefault();
        const th = e.currentTarget as HTMLTableCellElement;
        const side = getDropSide(th, e.clientX);
        setDropPreview({ targetId, side });
    };

    const handleDragLeave = () => setDropPreview(null);

    const handleDrop = (targetId: string) => (e: DragEvent) => {
        e.preventDefault();
        const srcId = e.dataTransfer.getData('text/x-col-id');
        if (!srcId || srcId === targetId) {
            setDraggingId(null);
            setDropPreview(null);
            return;
        }
        const side = dropPreview?.side ?? 'left';
        const current = orderedColumns.map(c => c.id);
        const from = current.indexOf(srcId);
        let to = current.indexOf(targetId);
        if (side === 'right') to = to + 1;
        const next = reorder(current, from, to > from ? to - 1 : to);
        setColOrder(next);
        setDraggingId(null);
        setDropPreview(null);
    };

    const handleDragEnd = () => {
        setDraggingId(null);
        setDropPreview(null);
    };

    // useEffect(() => {
    //     const idsNow = allColumns.map(c => c.id);
    //     setColOrder(prev => {
    //         const kept = prev.filter(id => idsNow.includes(id));
    //         const toAdd = idsNow.filter(id => !kept.includes(id));
    //         return [...kept, ...toAdd];
    //     });
    // }, [allColumns]);

    //#region  //bộ lọc

    const openModalFilter = (columnCode?: string) => {
        if (!columnCode) return
        setSelectedColumnFilter(columnCode)
    }

    const closeModalFilter = () => {
        setSelectedColumnFilter(undefined)
    }

    const checkIsFilter = (columnCode: string) => {
        switch (columnCode) {
            case 'own_vehicle_status': {
                return !!dataFilterList?.status?.length
            }
            default:
                break;
        }
    }

    //#endregion

    useEffect(() => {
        setColOrder(allColumns.map(c => c.id));
    }, [allColumns]);


    return (
        <>
            <FilterHeaderVehicleVersionTable
                columnCode={selectedColumnFilter}
                dataFilterList={dataFilterList}
                setDataFilterList={setDataFilterList}
                closeModalFilter={closeModalFilter}
            />
            <div className={`dt-wrapper ${className ?? ''}`}>
                {/* Table */}
                <div className={`dt-table-container ${stickyHeader ? 'sticky' : ''}`} style={{ maxHeight: 610, overflowY: rows.length === 0 ? 'hidden' : 'scroll' }}>
                    <table ref={tableRef} className="custom-page-css dt-table bg-096DD9 w-full"

                    >
                        <thead className='bg-096DD9 w-full'>
                            <tr className='py-2 bg-096DD9 w-full'>
                                <th className="custom-page-css dt-cell dt-checkbox py-2 bg-096DD9" hidden={!ischeckBox}>
                                    <input
                                        aria-label="Select all"
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={toggleSelectAllPage}
                                    />
                                </th>
                                {orderedColumns.map((col, i) => {
                                    const isDragging = draggingId === col.id;
                                    const showLeft = dropPreview?.targetId === col.id && dropPreview?.side === 'left';
                                    const showRight = dropPreview?.targetId === col.id && dropPreview?.side === 'right';
                                    return (
                                        <th
                                            key={col.id}
                                            data-cid={col.id}
                                            // className="dt-cell"
                                            style={{
                                                flex: 1,
                                                width: widths[col.id],
                                                minWidth: widths[col.id],
                                                maxWidth: widths[col.id],
                                                borderBottom: '1px solid #096DD9'
                                            }}
                                            className="dt-cell dt-th bg-096DD9 text-white h-45px"
                                            // draggable
                                            // onDragStart={handleDragStart(col.id)}
                                            // onDragOver={handleDragOver(col.id)}
                                            // onDragLeave={handleDragLeave}
                                            // onDragEnd={handleDragEnd}
                                            // onDrop={handleDrop(col.id)}

                                            draggable={isDraggable(col.id)}
                                            onDragStart={isDraggable(col.id) ? handleDragStart(col.id) : undefined}
                                            onDragOver={isDraggable(col.id) ? handleDragOver(col.id) : undefined}
                                            onDragLeave={isDraggable(col.id) ? handleDragLeave : undefined}
                                            onDragEnd={isDraggable(col.id) ? handleDragEnd : undefined}
                                            onDrop={isDraggable(col.id) ? handleDrop(col.id) : undefined}
                                        >
                                            <button
                                                className={`dt-th-btn ${state.sort.columnId === col.id ? 'active' : ''}`}
                                            // disabled={!col.sortable || !!draggingId}

                                            // onClick={() => console.log(col.id)}
                                            >
                                                <span className="dt-th-text text-left text-white "
                                                    onClick={() => col.sortable && !draggingId && toggleSort(col.id)}
                                                >{col.header}</span>
                                                {
                                                    state.sort.columnId === col.id && (
                                                        <span className="dt-sort-indicator">
                                                            {state.sort.direction === 'asc' ? '▲' : '▼'}
                                                        </span>
                                                    )
                                                }
                                                {
                                                    filterColumn.includes(col.id) && (
                                                        <div
                                                            onClick={() => openModalFilter(col.id)}
                                                        >
                                                            {/* <FilterFilled /> */}
                                                            {
                                                                checkIsFilter(col.id) ?
                                                                    <FilterFilled className='text-white' /> :
                                                                    <FilterOutlined className='text-white' />
                                                            }
                                                        </div>
                                                    )

                                                }
                                                {
                                                    isResizable(col.id) && (
                                                        <span
                                                            className={`dt-resizer ${orderedColumns.length === i + 1 ? 'd-none' : ''}`}
                                                            onMouseDown={(e) => onMouseDown(col.id, e)}
                                                            onDoubleClick={() => onDoubleClick(col.id)}
                                                        />
                                                    )
                                                }
                                            </button>

                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {
                                loadingTable
                                    ?
                                    <CustomLoadingOverlayTable />
                                    :
                                    <>
                                        {
                                            rows.map((r: any, rowIndex) => {
                                                const extra = rowClassName ? rowClassName(r, rowIndex) : '';
                                                const [rowId, stt] = String(r.data?.id).split("-");
                                                const isSelected = (selectedRowId === rowId && !Number.isNaN(Number(rowId)))
                                                return (
                                                    <tr
                                                        key={r.id}
                                                        className={`${isSelected ? 'is-selected-custom' : ''} ${extra} clickable-row`}
                                                        onClick={() => handleRowClick(r, rowIndex)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td
                                                            className="dt-cell dt-checkbox"
                                                            hidden={!ischeckBox}
                                                            onClick={(e) => e.stopPropagation()} // Ngăn sự kiện click lan ra row
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => toggleSelectRow(r.id)}
                                                            />
                                                        </td>
                                                        {
                                                            orderedColumns.map(col => {
                                                                const raw = col.accessor ? col.accessor(r.data) : (r.data as any)[col.id];
                                                                const value = (raw as any)?.props?.children ?? raw;
                                                                const editing = state.editingCell?.rowId === r.id && state.editingCell?.columnId === col.id;
                                                                const onChange = (next: any) => {
                                                                    (r.data as any)[col.id] = next;
                                                                };
                                                                return (
                                                                    <td
                                                                        key={col.id}
                                                                        className="dt-cell"
                                                                        style={{ width: widths[col.id] }}
                                                                        onDoubleClick={() => col.editable && startEdit(r.id, col.id)}
                                                                    >
                                                                        {
                                                                            editing && col.editable ? (
                                                                                <input
                                                                                    className="dt-input dt-edit"
                                                                                    autoFocus
                                                                                    defaultValue={value ?? ''}
                                                                                    onKeyDown={(e) => {
                                                                                        if (e.key === 'Enter') {
                                                                                            onChange((e.target as HTMLInputElement).value);
                                                                                            stopEdit();
                                                                                        } else if (e.key === 'Escape') {
                                                                                            stopEdit();
                                                                                        }
                                                                                    }}
                                                                                    onBlur={() => stopEdit()}
                                                                                />
                                                                            ) : (
                                                                                col.renderCell
                                                                                    ? col.renderCell({ value, row: r.data, rowIndex, editing, onChange })
                                                                                    : <span>{String(value ?? '')}</span>
                                                                            )
                                                                        }
                                                                    </td>
                                                                );
                                                            })
                                                        }
                                                    </tr>
                                                );
                                            })
                                        }
                                        {
                                            rows.length === 0 && (
                                                <tr>
                                                    <td className="dt-cell dt-empty" colSpan={orderedColumns.length + 1}>
                                                        <div
                                                            style={{
                                                                textAlign: 'center',
                                                                padding: 20
                                                            }}
                                                        >
                                                            <br />
                                                            <img src={noData} alt="No data"
                                                                style={{
                                                                    maxWidth: 150
                                                                }}
                                                            />
                                                            <br />
                                                            <div>Không có dữ liệu</div>
                                                            <br />
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </>
                            }

                        </tbody>
                    </table>
                </div>
            </div>
        </>

    );
}
